import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';

import { Button, TextButton } from 'components/core';
import useTranslation from 'hooks/use-translation';
import { Typography } from 'src/typography';
import useErnie from 'shared/hooks/use-ernie';
import { ERNIE_TYPES } from 'shared/constants';
import { DeleteAccountDocument } from 'src/gql/graphql';

type Props = {
  onClose: () => void;
  afterDelete: () => void;
};

export function DeleteAccountModal({ onClose, afterDelete }: Props): JSX.Element {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const showErnie = useErnie();
  const isMounted = useRef(true);
  const [deleteAccountMutation] = useMutation(DeleteAccountDocument);

  const handleDelete = async (): Promise<void> => {
    setIsDeleting(true);

    try {
      const { data } = await deleteAccountMutation();
      if (data?.deleteAccount.success) {
        showErnie(t('account.delete.success', 'Account deleted successfully'), ERNIE_TYPES.SUCCESS);
        afterDelete();
      } else {
        throw new Error(data?.deleteAccount.error ?? 'Failed to delete account');
      }
    } catch (error) {
      if (isMounted.current) {
        showErnie(t('account.delete.error', error.message || 'Failed to delete account'), ERNIE_TYPES.DANGER);
        setIsDeleting(false);
      }
    }
  };

  return (
    <>
      <Typography.Heading tag='h2' size='medium'>
        {t('account.delete.modal.title', 'Delete Account')}
      </Typography.Heading>
      <ContentContainer>
        <Typography.Body size='medium'>
          {t(
            'account.delete.modal.message',
            'Are you sure you want to delete your account? This action cannot be undone.'
          )}
        </Typography.Body>
      </ContentContainer>
      <ButtonContainer>
        <Button variant='primary' onClick={onClose} disabled={isDeleting}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <DeleteButton onClick={handleDelete} disabled={isDeleting}>
          {isDeleting ? t('account.delete.modal.deleting', 'Deleting...') : t('account.delete.modal.confirm', 'Delete')}
        </DeleteButton>
      </ButtonContainer>
    </>
  );
}

const ContentContainer = styled.div`
  margin: 16px 0 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 16px;
  padding: 0 20px;

  > button {
    display: block;
  }
`;

const DeleteButton = styled(TextButton)`
  color: #3387e1;
`;
